import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, KeyRound } from "lucide-react";
import { contactHref, money, planBullets, priceLabel, showPerMonth } from "@/lib/plans";
import { FEATURES } from "@/lib/features";
import { Button } from "@/components/ui/button";

export const Features = () => (
  <section id="features" className="mx-auto max-w-6xl px-6 py-24" data-testid="home-features">
    <p className="font-data text-xs uppercase tracking-[0.2em] text-blue-400">Features</p>
    <h2 className="font-display mt-2 max-w-xl text-3xl font-extrabold tracking-tight text-white sm:text-4xl">Warrant runs, pre-warrant visits and every detail your team needs to keep work moving</h2>
    <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
      {FEATURES.map(({ icon: Icon, title, text, slug }, i) => (
        <Link key={slug} to={`/features/${slug}`} className="home-rise group block rounded-xl border border-slate-200 bg-white p-8 shadow-xl transition-transform hover:-translate-y-1" style={{ animationDelay: `${i * 70}ms` }} data-testid={`home-feature-${i}`}>
          <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-blue-50 text-blue-700 transition-colors group-hover:bg-blue-700 group-hover:text-white"><Icon className="h-5 w-5" /></span>
          <h3 className="font-display mt-4 text-lg font-bold text-slate-900 group-hover:text-blue-700">{title}</h3>
          <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{text}</p>
          <span className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-blue-700">Learn more <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" /></span>
        </Link>
      ))}
    </div>
    <Link to="/features" className="mt-12 inline-flex items-center gap-1 font-semibold text-blue-400 hover:underline" data-testid="home-all-features-link">See all features <ArrowRight className="h-4 w-4" /></Link>
  </section>
);

const PlanCard = ({ p, i }) => (
  <div className={`home-rise relative flex flex-col rounded-xl border bg-white p-8 shadow-xl transition-transform hover:-translate-y-1 ${p.featured ? "border-blue-600 ring-2 ring-blue-600" : "border-slate-200"}`}
    style={{ animationDelay: `${i * 90}ms` }} data-testid={`home-plan-${p.id}`}>
    <div className="flex items-center justify-between gap-2">
      <p className="font-display text-xl font-bold text-slate-900">{p.name}</p>
      {p.featured && <span className="rounded-full bg-blue-700 px-2.5 py-0.5 text-xs font-semibold text-white" data-testid={`home-plan-featured-${p.id}`}>Most popular</span>}
    </div>
    <p className="font-display mt-4 text-4xl font-extrabold text-slate-900" data-testid={`home-plan-price-${p.id}`}>{priceLabel(p)}{showPerMonth(p) && <span className="text-base font-medium text-slate-500"> / month</span>}</p>
    {p.description && <p className="mt-2 text-sm text-slate-500">{p.description}</p>}
    <ul className="mt-6 flex-1 space-y-2 text-sm text-slate-700">
      {planBullets(p, ["14-day free trial"]).map((t) => (
        <li key={t} className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 shrink-0 text-blue-700" /> {t}</li>))}
    </ul>
    {p.display_only
      ? <Button asChild variant="outline" className="mt-7" data-testid={`home-plan-contact-${p.id}`}><a href={contactHref(p)}>Contact us</a></Button>
      : <Button asChild className="mt-7 bg-blue-700 hover:bg-blue-800" data-testid={`home-plan-cta-${p.id}`}><Link to="/signup">Start free trial</Link></Button>}
  </div>
);

export const Pricing = ({ plans }) => (
  <section id="pricing" className="bg-slate-950 py-24" data-testid="home-pricing">
    <div className="mx-auto max-w-6xl px-6">
      <p className="font-data text-xs uppercase tracking-[0.2em] text-blue-400">Pricing</p>
      <h2 className="font-display mt-2 text-3xl font-extrabold tracking-tight text-white sm:text-4xl">Simple monthly plans.</h2>
      <p className="mt-2 max-w-lg text-slate-400">{plans?.some((p) => p.display_only) ? "Monthly plans start with a 14-day free trial. No card needed to start, cancel any time." : "Every plan starts with a 14-day free trial. No card needed to start, cancel any time."}</p>
      {plans === null && <p className="mt-10 text-sm text-slate-500" data-testid="home-plans-loading">Loading plans…</p>}
      {plans?.length === 0 && <p className="mt-10 text-sm text-slate-400" data-testid="home-plans-empty">Plans are coming soon.</p>}
      <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {plans?.map((p, i) => <PlanCard key={p.id} p={p} i={i} />)}
      </div>
    </div>
  </section>
);

export const FinalCta = () => (
  <section className="mx-auto max-w-6xl px-6 py-24" data-testid="home-final-cta">
    <div className="flex flex-col items-start justify-between gap-6 rounded-xl border border-slate-200 bg-white p-10 shadow-xl md:flex-row md:items-center">
      <div>
        <KeyRound className="h-8 w-8 text-blue-700" />
        <h2 className="font-display mt-3 text-2xl font-extrabold text-slate-900 sm:text-3xl">Get your team’s warrant work organised today.</h2>
        <p className="mt-1 text-slate-600">Set up your company in under two minutes.</p>
      </div>
      <Button asChild size="lg" className="bg-blue-700 hover:bg-blue-800" data-testid="home-final-signup"><Link to="/signup">Start free trial <ArrowRight className="ml-1 h-4 w-4" /></Link></Button>
    </div>
  </section>
);
